import {
	RoyalProvisionRewardKey,
	royalProvisionRewards,
} from "@/game/config/shop";
import { QuestObjectiveActions } from "@/game/entities/config/quests/quest_objective_actions";
import { ResourceType } from "@/game/entities/config/resources";
import QuestRepository from "@/game/QuestRepository";
import GameError, { GameErrorType } from "@/libs/GameError";
import { type Timestamp } from "@/types";
import { ceilNumber, floorNumber } from "@/utils/number";
import {
	createImmutableState,
	getImmutableState,
	getImmutableStateProperty,
	setImmutableState,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { computed, type ComputedRef, watch } from "vue";
import useUserStore from "./useUserStore";

export type ResourcesStoreState = Record<ResourceType, number>;

type ResourcesStoreDiffAction = {
	type: "set" | "increase" | "decrease";
	key: ResourceType;
	currentValue: number;
	value: number;
	reason?: string | false;
	timestamp: Timestamp;
};

type ResourcesStorePayload = {
	state: ResourcesStoreState;
	actions: Array<ResourcesStoreDiffAction>;
};

export type ResourcesStoreGetters = Record<ResourceType, ComputedRef<number>>;

type ResourcesStore = ResourcesStoreGetters & {
	$getPayload: () => ResourcesStorePayload | null;
	$resetPayload: () => void;
	$setState: (value: Partial<ResourcesStoreState>) => void;
	set: (type: ResourceType, value: number, reason?: string | false) => void;
	getCurrencyValue: (type: ResourceType) => number;
	increase: (
		type: ResourceType,
		value: number,
		reason?: string | false,
	) => void;
	decrease: (
		type: ResourceType,
		value: number,
		reason?: string | false,
	) => void;
	validateResourceAmount: (type: ResourceType, value: number) => boolean;
};

const getDefaultValue = (type: ResourceType): number => {
	switch (type) {
		case ResourceType.COINS:
			return 10_000;
		case ResourceType.GEMS:
			return 250;
		case ResourceType.EXPLORATION_SPEED:
			return 1;
		default:
			return 0;
	}
};

export const TRACKABLE_RESOURCE_TYPES = new Set<ResourceType>([
	// ResourceType.COINS,
	ResourceType.GEMS,
	ResourceType.AD_SKIPPER,
	ResourceType.CLICKER_2,
	ResourceType.CLICKER_3,
	ResourceType.CLICKER_5,
	ResourceType.SUPER_CLICKER,
	ResourceType.EVENTIUM,
]);

export const UNCAPPED_RESOURCE_TYPES = new Set<ResourceType>([
	ResourceType.FOOD_DIFF,
	ResourceType.POPULATION_DIFF,
	ResourceType.COINS,
	ResourceType.EVENTIUM,
]);

const STORE_NAME = "resources";

const CONSOLE_COLOR = [
	"%c STORE ACTION ",
	"background-color:indianred;color:white;font-weight:700;",
];

export default defineStore(STORE_NAME, (): ResourcesStore => {
	const keys = (Object.values(ResourceType) as Array<ResourceType>).filter(
		(key: ResourceType): boolean =>
			key === ResourceType.FISH_INCOME ||
			(![
				ResourceType.LEVEL,
				ResourceType.SETTLEMENT_CONNECTION,
				ResourceType.SETTLEMENT_CLICKS,
			].includes(key) &&
				!key.endsWith("Coefficient") &&
				// !key.endsWith("Unit") &&
				!key.endsWith("Multiplier")),
	);

	const state = createImmutableState<ResourcesStoreState>(
		keys.reduce(
			(
				acc: ResourcesStoreState,
				key: ResourceType,
			): ResourcesStoreState => ({
				...acc,
				[key]: getDefaultValue(key),
			}),
			{} as ResourcesStoreState,
		),
	);

	// Used only for save.
	// DO NOT USE IN GAME!
	const $isKeyValid = (key: ResourceType): boolean =>
		(!key.endsWith("Income") &&
			!key.endsWith("Consumption") &&
			(!key.endsWith("UncappedIncomeWithoutPenalties") ||
				key ===
					ResourceType.EVENTIUM_UNCAPPED_INCOME_WITHOUT_PENALTIES) &&
			!key.endsWith("Coefficient") &&
			// !key.endsWith("Unit") &&
			!key.endsWith("Multiplier")) ||
		key === "coinsIncome" ||
		key.endsWith("Plus") ||
		key.endsWith("Minus") ||
		key.endsWith("Total");

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getState = (): ResourcesStoreState => {
		const data = getImmutableState(state).value;
		const keys = Object.keys(data) as Array<ResourceType>;
		return keys.reduce(
			(
				acc: ResourcesStoreState,
				key: ResourceType,
			): ResourcesStoreState => {
				if (!$isKeyValid(key)) return acc;

				const value = data[key] ?? 0;

				// Leaderboards should not be affected by temporary income boost.
				// In-game calculations use boosted coinsIncome, but for persistence we store base.
				if (key === ResourceType.COINS_INCOME) {
					return {
						...acc,
						[key]: ceilNumber(value),
					};
				}

				if (key === ResourceType.EVENTIUM_INCOME) {
					return {
						...acc,
						[key]: ceilNumber(value),
					};
				}

				return {
					...acc,
					[key]: value,
				};
			},
			{} as ResourcesStoreState,
		);
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $actions = new Array<ResourcesStoreDiffAction>();

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getActions = (): Array<ResourcesStoreDiffAction> =>
		$actions.splice(0, $actions.length);

	// Used only for save.
	// DO NOT USE IN GAME!
	let $isPatched = false;

	// Used only for save.
	// DO NOT USE IN GAME!
	let $payloadInProgress: ResourcesStorePayload | null = null;

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getPayload = (): ResourcesStorePayload | null => {
		if (!$isPatched) return null;
		if ($payloadInProgress !== null) return $payloadInProgress;

		return ($payloadInProgress = {
			state: $getState(),
			actions: $getActions(),
		});
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $resetPayload = (): void => {
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Partial<ResourcesStoreState>): void => {
		setImmutableState(state, value);
		setTimeout((): void => {
			$isPatched = true;
		}, 0);
	};

	const getters = keys.reduce(
		(
			acc: ResourcesStoreGetters,
			key: ResourceType,
		): ResourcesStoreGetters => {
			if (key === ResourceType.EXPLORATION_SPEED) {
				return {
					...acc,
					[key]: computed((): number => {
						const baseSpeed = getImmutableStateProperty(
							state,
							key,
							1,
						).value;
						const userStore = useUserStore();

						const activeProvisions = userStore.activeProvisions;
						const activeProvisionsCount = activeProvisions.length;

						if (activeProvisionsCount > 0) {
							// Get the base multiplier from the config (e.g. 1.3 from ExploringPowerBoost,30%)
							const configBonus =
								royalProvisionRewards[
									RoyalProvisionRewardKey.ExploringPowerBoost
								].value ?? 1;

							// Calculate bonus increment per package (e.g., 1.3 -> 0.3 bonus)
							const bonusPerPackage = configBonus - 1;

							// Total multiplier based on the number of active packages
							const totalMultiplier =
								1 + bonusPerPackage * activeProvisionsCount;

							// NOTE: Used floor instead of ceil per specification
							return floorNumber(baseSpeed * totalMultiplier);
						}

						return baseSpeed;
					}),
				};
			}

			return {
				...acc,
				[key]: getImmutableStateProperty(state, key, 0),
			};
		},
		{} as ResourcesStoreGetters,
	);

	const set = (
		key: ResourceType,
		value: number,
		reason?: string | false,
	): void => {
		if (isNaN(value))
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Invalid value for resource ${key}: ${value}`,
			);

		const currentValue = state.value[key] ?? 0;
		if (currentValue === value) return;

		if (
			![ResourceType.FOOD_DIFF, ResourceType.POPULATION_DIFF].includes(
				key,
			) &&
			value < 0
		)
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Cannot set ${key} below zero!`,
			);

		setImmutableStateProperty(state, key, value);

		if (
			$isKeyValid(key) &&
			reason !== false &&
			TRACKABLE_RESOURCE_TYPES.has(key)
		)
			$actions.push({
				type: "set",
				key,
				currentValue,
				value,
				reason,
				timestamp: Date.now(),
			});

		if ($isKeyValid(key) && reason === undefined) {
			// eslint-disable-next-line no-console
			console.trace(
				...CONSOLE_COLOR,
				"[ResourcesStore] TREBA DOPLNIT REASON",
			);
		}
	};

	const increase = (
		key: ResourceType,
		value: number,
		reason?: string | false,
	): void => {
		if (isNaN(value))
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Invalid value for resource ${key}: ${value}`,
			);

		const currentValue = state.value[key] ?? 0;
		if (value === 0) return;

		setImmutableStateProperty(state, key, currentValue + value);

		if (key === ResourceType.EVENTIUM) {
			setImmutableStateProperty(
				state,
				ResourceType.EVENTIUM_ACCUMULATED,
				(state.value[ResourceType.EVENTIUM_ACCUMULATED] ?? 0) + value,
			);
		}
		if (
			$isKeyValid(key) &&
			reason !== false &&
			TRACKABLE_RESOURCE_TYPES.has(key)
		)
			$actions.push({
				type: "increase",
				key,
				currentValue,
				value,
				reason,
				timestamp: Date.now(),
			});

		if ($isKeyValid(key) && reason === undefined) {
			// eslint-disable-next-line no-console
			console.trace(
				...CONSOLE_COLOR,
				"[ResourcesStore] TREBA DOPLNIT REASON",
			);
		}
	};

	const decrease = (
		key: ResourceType,
		value: number,
		reason?: string | false,
		isTickCalculation?: boolean,
	): void => {
		if (isNaN(value))
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Invalid value for resource ${key}: ${value}`,
			);

		const currentValue = state.value[key] ?? 0;
		if (value === 0) return;

		const targetValue = currentValue - value;
		if (targetValue < 0 && !isTickCalculation)
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Cannot decrease ${key} below zero!`,
			);

		setImmutableStateProperty(state, key, targetValue);
		const questRepo = QuestRepository.getInstance();
		questRepo.manageQuestState(
			QuestObjectiveActions.SPEND_RESOURCES,
			null,
			value,
		);
		if (key === ResourceType.AD_SKIPPER) {
			questRepo.manageQuestState(
				QuestObjectiveActions.WATCH_ADS,
				null,
				1,
			);
		}
		if (
			$isKeyValid(key) &&
			reason !== false &&
			TRACKABLE_RESOURCE_TYPES.has(key)
		)
			$actions.push({
				type: "decrease",
				key,
				currentValue,
				value,
				reason,
				timestamp: Date.now(),
			});

		if ($isKeyValid(key) && reason === undefined) {
			// eslint-disable-next-line no-console
			console.trace(
				...CONSOLE_COLOR,
				"[ResourcesStore] TREBA DOPLNIT REASON",
			);
		}
	};

	const getCurrencyValue = (currency: ResourceType): number =>
		state.value[currency] ?? 0;

	const validateResourceAmount = (
		key: ResourceType,
		value: number,
	): boolean => {
		const storedValue = state.value[key] ?? 0;
		return storedValue >= value;
	};

	const analyticsResourceTypeSet = new Set<ResourceType>([
		ResourceType.COINS,
		ResourceType.GEMS,
		ResourceType.CLICKER_2,
		ResourceType.CLICKER_3,
		ResourceType.CLICKER_5,
		ResourceType.SUPER_CLICKER,
		ResourceType.AD_SKIPPER,
		ResourceType.EVENTIUM,
	]);

	(Object.keys(state.value) as Array<keyof ResourcesStoreState>).forEach(
		(key: keyof ResourcesStoreState): void => {
			watch(
				(): ResourcesStoreState[keyof ResourcesStoreState] =>
					state.value[key],
				(
					value: ResourcesStoreState[keyof ResourcesStoreState],
					currentValue: ResourcesStoreState[keyof ResourcesStoreState],
				): void => {
					if (!$isPatched) return;
					if (!analyticsResourceTypeSet.has(key)) return;

					const diffValue = value - currentValue;
					if (diffValue === 0) return;

					const computedKey = (key +
						(diffValue > 0 ? "Plus" : "Minus")) as ResourceType;

					setImmutableStateProperty(
						state,
						computedKey,
						(state.value[computedKey] ?? 0) + Math.abs(diffValue),
					);
				},
			);
		},
	);

	return {
		$getPayload,
		$resetPayload,
		$setState,
		...getters,
		getCurrencyValue,
		set,
		increase,
		decrease,
		validateResourceAmount,
	};
});
